import React, { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import Footer from "../../../core/common/footer/Footer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import z from "zod";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { apibaseurl } from "../../../environment";
import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
import { all_routes } from "../../router/all_routes";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import bulbImage from "../../../images/buld.png";
import {
  disabledStatuses,
  getTodayDate,
  getYesterdayDate,
} from "../../../core/common/DateValidHelper";

type EmploymentRecord = Record<string, any>;

const EmployementHistory = () => {
  const templates = useSelector((state: RootState) => state.package.templates);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { authData } = useAuth();

  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};

  const EMPLOYMENT_CHECK_NAMES = [
    "Employment - 2 Year",
    "Employment - 3 Year",
    "Employment - 5 Year",
    "Employment - 7 Year",
    "Employment - 10 Year",
  ];

  const employmentTemplate = templates.find((item: any) =>
    EMPLOYMENT_CHECK_NAMES.includes(item.check_name),
  );

  const assignmentTemplateId = employmentTemplate?.assignment_template_id;

  const caseId = employmentTemplate?.case_id;
  const assignmentId = employmentTemplate?.assignment_id;
  const candidateId = authData?.candidate?.candidateId;
  const templateStatus = employmentTemplate?.status;

  const [formFields, setFormFields] = useState<any[]>([]);
  const [checkDetails, setCheckDetails] = useState<any>();

  const [employmentHistory, setEmploymentHistory] = useState<
    EmploymentRecord[]
  >([]);

  const [employmentUploadedFiles, setEmploymentUploadedFiles] = useState<
    Record<string, any>[]
  >([]);

  const [noDocument, setNoDocument] = useState<Record<string, boolean>>({});

  const [isFresher, setIsFresher] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [collapsedEmployments, setCollapsedEmployments] = useState<
    Record<number, boolean>
  >({});

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);



  const isFormDisabled = disabledStatuses.includes(templateStatus ?? "");
  const form_type_new = formFields?.[0]?.form_type_new;

  const createEmptyEmployment = (fields: any[]): EmploymentRecord => {
    const record: EmploymentRecord = {};

    fields.forEach((field: any) => {
      record[field.field_id] = null;
    });

    return record;
  };
  const getRecordKey = (employmentIndex: number, fieldId: string) =>
    `${employmentIndex}_${fieldId}`;

  const PF_REGEX = /^[A-Z]{2}[\s/]?[A-Z]{3}[\s/]?\d{7}[\s/]?\d{3}[\s/]?\d{7}$/;
  const UAN_REGEX = /^\d{12}$/;

  const getStatutoryError = (fieldId: string, value: any): string => {
    if (fieldId !== "uan_number" && fieldId !== "pf_verification_number")
      return "";
    const v = String(value ?? "").trim();
    if (v.toUpperCase() === "N/A") return "";
    if (fieldId === "uan_number" && !UAN_REGEX.test(v))
      return "UAN must be exactly 12 digits";
    if (fieldId === "pf_verification_number" && !PF_REGEX.test(v.toUpperCase()))
      return "Enter a valid PF number (e.g. MH/BAN/0000000/000/0000000)";
    return "";
  };

  const generateSchema = (fields: any[], employmentIndex: number) => {
    const schemaFields: Record<string, any> = {};

    fields.forEach((field) => {
      let validator: any = z.any();

      if (field.type === "file") {
        const key = getRecordKey(employmentIndex, field.field_id);

        const skipValidation = noDocument?.[key] === true;
        const isMultiple = field.file_config?.multiple === true;

        const existingFiles =
          employmentUploadedFiles[employmentIndex]?.[field.field_id];

        const hasExistingFiles =
          Array.isArray(existingFiles) && existingFiles.length > 0;

        if (skipValidation || hasExistingFiles) {
          validator = z.any().optional();
        } else if (isMultiple) {
          validator = field.required
            ? z.array(z.instanceof(File)).min(1, `${field.label} is required`)
            : z.array(z.instanceof(File)).optional();
        } else {
          validator = field.required
            ? z.instanceof(File, {
                message: `${field.label} is required`,
              })
            : z.any().optional();
        }
      } else {
        const isStatutory = ["uan_number", "pf_verification_number"].includes(
          field.field_id,
        );
        // Keep string validation separate to preserve TypeScript types
        let stringValidator: z.ZodString = z.string();

        if (field.required) {
          stringValidator = stringValidator.min(
            1,
            `${field.label} is required`,
          );
        }

        if (!isStatutory && field.validation_rules?.minLength) {
          stringValidator = stringValidator.min(
            field.validation_rules.minLength,
            `${field.label} must be at least ${field.validation_rules.minLength} characters`,
          );
        }

        if (!isStatutory && field.validation_rules?.maxLength) {
          stringValidator = stringValidator.max(
            field.validation_rules.maxLength,
            `${field.label} cannot exceed ${field.validation_rules.maxLength} characters`,
          );
        }

        if (!isStatutory && field.validation_rules?.pattern) {
          stringValidator = stringValidator.regex(
            new RegExp(field.validation_rules.pattern),
            `${field.label} is invalid`,
          );
        }

        validator = field.required ? stringValidator : z.string().optional();
      }

      schemaFields[field.field_id] = validator;
    });

    return z.object(schemaFields).superRefine((data, ctx) => {
      const joining = data.date_of_joining;
      const relieving = data.date_of_relieving;

      // 1. Joining Date cannot be in the future
      if (isValidDate(joining) && joining > getTodayDate()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["date_of_joining"],
          message: "Date of Joining cannot be a future date",
        });
      }

      // 2. Relieving Date must be before today
      if (isValidDate(relieving) && relieving >= getTodayDate()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["date_of_relieving"],
          message: "Date of Relieving must be before today",
        });
      }

      // 3. Relieving Date cannot be before Joining Date
      if (
        isValidDate(joining) &&
        isValidDate(relieving) &&
        relieving < joining
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["date_of_relieving"],
          message: "Date of Relieving cannot be before Date of Joining",
        });
      }

      ["uan_number", "pf_verification_number"].forEach((id) => {
        const field = fields.find((item: any) => item.field_id === id);
        if (!field) return;
        const message = getStatutoryError(id, data[id]);
        if (message)
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [id], message });
      });
    });
  };

  const formFieldsAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateSaveAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateUpdateAPI = useApi<any>(
    `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
    {
      method: "PUT",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const employementForm = async () => {
    try {
      const response = await formFieldsAPI.callApi();

      if (response?.success) {
        const checkdetails = response?.data?.check;

        handleEmploymentResponse(response);

        setCheckDetails(checkdetails);
        setErrors({});
        setCollapsedEmployments({});
        setNoDocument({});
      }
    } catch (error) {
      console.error("Error fetching form fields:", error);
    }
  };

  const formLoading = formFieldsAPI.loading;

  useEffect(() => {
    if (assignmentTemplateId) {
      employementForm();
    }
  }, [assignmentTemplateId]);

  const handleEmploymentResponse = (response: any) => {
    const fields = response?.data?.fields || [];

    if (!fields.length) {
      setFormFields([]);
      setEmploymentHistory([]);
      setEmploymentUploadedFiles([]);
      return;
    }

    // AFTER SAVE / EXISTING EMPLOYMENT RECORDS
    if (fields[0]?.fields && Array.isArray(fields[0].fields)) {
      setFormFields(fields[0].fields);

      const employmentRecords = fields.map((entry: any) => {
        const record: Record<string, any> = {};

        entry.fields.forEach((field: any) => {
          record[field.field_id] = field.value ?? null;
        });

        return record;
      });

      // Store uploaded files separately for each employment record
      const uploadedFilesByEmployment = fields.map((entry: any) => {
        const files: Record<string, any> = {};

        entry.fields.forEach((field: any) => {
          if (
            Array.isArray(field.uploaded_files) &&
            field.uploaded_files.length > 0
          ) {
            files[field.field_id] = field.uploaded_files;
          }
        });

        return files;
      });

      setEmploymentHistory(employmentRecords);
      setEmploymentUploadedFiles(uploadedFilesByEmployment);

      return;
    }

    // FIRST TIME
    setFormFields(fields);

    setEmploymentHistory([createEmptyEmployment(fields)]);

    // No uploaded files for a new/blank employment
    setEmploymentUploadedFiles([{}]);
  };

  const handleFieldChange = async (
    employmentIndex: number,
    fieldId: string,
    value: any,
  ) => {
    if (fieldId === "uan_number") {
      const raw = String(value ?? "");
      value =
        raw.trim().toUpperCase() === "N/A"
          ? "N/A"
          : raw.replace(/\D/g, "").slice(0, 12);
    }
    if (fieldId === "pf_verification_number") {
      const raw = String(value ?? "");
      value =
        raw.trim().toUpperCase() === "N/A"
          ? "N/A"
          : raw
              .toUpperCase()
              .replace(/[^A-Z0-9/\s]/g, "")
              .slice(0, 26);
    }

    // Date validation for the current employment record
    if (fieldId === "date_of_joining" || fieldId === "date_of_relieving") {
      const currentEmployment = employmentHistory[employmentIndex] || {};

      const joining =
        fieldId === "date_of_joining"
          ? value
          : currentEmployment.date_of_joining;

      const relieving =
        fieldId === "date_of_relieving"
          ? value
          : currentEmployment.date_of_relieving;

      const errorKey = getRecordKey(employmentIndex, fieldId);

      let dateError = "";

      if (
        fieldId === "date_of_joining" &&
        isValidDate(value) &&
        value > getTodayDate()
      ) {
        dateError = "Date of Joining cannot be a future date";
      } else if (
        fieldId === "date_of_relieving" &&
        isValidDate(value) &&
        value >= getTodayDate()
      ) {
        dateError = "Date of Relieving must be before today";
      } else if (
        isValidDate(joining) &&
        isValidDate(relieving) &&
        relieving < joining
      ) {
        dateError =
          fieldId === "date_of_relieving"
            ? "Date of Relieving cannot be before Date of Joining"
            : "Date of Joining cannot be after Date of Relieving";
      }

      if (dateError) {
        setEmploymentHistory((prev) =>
          prev.map((record, index) =>
            index === employmentIndex ? { ...record, [fieldId]: "" } : record,
          ),
        );

        setErrors((prev) => ({
          ...prev,
          [errorKey]: dateError,
        }));

        return;
      }
    }

    // KEEP YOUR EXISTING CODE BELOW UNCHANGED

    setEmploymentHistory((prev) => {
      const updated = [...prev];

      const currentRecord = {
        ...(updated[employmentIndex] || {}),
      };

      currentRecord[fieldId] = value;

      // Supervisor Details
      if (fieldId === "supervisor_details_available") {
        if (value === "NO") {
          currentRecord.reporting_manager_name = "N/A";
          currentRecord.reporting_manager_email = "N/A";
          currentRecord.reporting_manager_mobile = "N/A";
          currentRecord.reporting_manager_designation = "N/A";
        } else {
          currentRecord.reporting_manager_name = "";
          currentRecord.reporting_manager_email = "";
          currentRecord.reporting_manager_mobile = "";
          currentRecord.reporting_manager_designation = "";
        }
      }

      // HR Details
      if (fieldId === "hr_details_available") {
        if (value === "NO") {
          currentRecord.hr_name = "N/A";
          currentRecord.hr_contact_details = "N/A";
          currentRecord.hr_email_id = "N/A";
        } else {
          currentRecord.hr_name = "";
          currentRecord.hr_contact_details = "";
          currentRecord.hr_email_id = "";
        }
      }

      // Pincode
      if (fieldId === "pincode" && String(value).length < 6) {
        currentRecord.organization_state = "";
        currentRecord.organization_city = "";
      }

      updated[employmentIndex] = currentRecord;

      return updated;
    });

    const errorKey = getRecordKey(employmentIndex, fieldId);

    setErrors((prev) => ({
      ...prev,
      [errorKey]: getStatutoryError(fieldId, value),
    }));

    if (fieldId === "pincode" && /^[0-9]{6}$/.test(String(value))) {
      await getLocationByPincode(employmentIndex, value);
    }
  };

  const handleNoDocumentChange = (
    employmentIndex: number,
    fieldId: string,
    checked: boolean,
  ) => {
    const key = getRecordKey(employmentIndex, fieldId);

    setNoDocument((prev) => ({
      ...prev,
      [key]: checked,
    }));

    if (checked) {
      handleFieldChange(employmentIndex, fieldId, null);

      if (fileInputRefs.current[key]) {
        fileInputRefs.current[key]!.value = "";
      }

      setErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
  };

  const getLocationByPincode = async (
    employmentIndex: number,
    pincode: string,
  ) => {
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );

      const data = await response.json();

      if (
        data?.[0]?.Status === "Success" &&
        data?.[0]?.PostOffice?.length > 0
      ) {
        const postOffice = data[0].PostOffice[0];

        setEmploymentHistory((prev) => {
          const updated = [...prev];

          updated[employmentIndex] = {
            ...(updated[employmentIndex] || {}),
            organization_country: postOffice.Country || "India",
            organization_state: postOffice.State || "",
            organization_city:
              postOffice.District ||
              postOffice.Division ||
              postOffice.Block ||
              "",
          };

          return updated;
        });

        setErrors((prev) => ({
          ...prev,
          [getRecordKey(employmentIndex, "pincode")]: "",
          [getRecordKey(employmentIndex, "organization_country")]: "",
          [getRecordKey(employmentIndex, "organization_state")]: "",
          [getRecordKey(employmentIndex, "organization_city")]: "",
        }));
      } else {
        setEmploymentHistory((prev) => {
          const updated = [...prev];

          updated[employmentIndex] = {
            ...(updated[employmentIndex] || {}),
            organization_country: "",
            organization_state: "",
            organization_city: "",
          };

          return updated;
        });
      }
    } catch (error) {
      console.error("Pincode API error:", error);
    }
  };

  // const addEmployment = () => {
  //   const newRecord = createEmptyEmployment(formFields);

  //   setEmploymentHistory((prev) => [...prev, newRecord]);

  //   setCollapsedEmployments((prev) => ({
  //     ...prev,
  //     [prev ? employmentHistory.length : 0]: false,
  //   }));
  // };
  const addEmployment = () => {
    const newRecord = createEmptyEmployment(formFields);

    setEmploymentHistory((prev) => [...prev, newRecord]);

    // New employment has no uploaded files
    setEmploymentUploadedFiles((prev) => [...prev, {}]);

    setCollapsedEmployments((prev) => ({
      ...prev,
      [employmentHistory.length]: false,
    }));
  };
  const removeEmployment = (employmentIndex: number) => {
    if (employmentHistory.length === 1) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Remove",
        text: "At least one employment record is required.",
      });

      return;
    }

    Swal.fire({
      icon: "warning",
      title: "Remove Employment?",
      text: `Are you sure you want to remove Employment ${
        employmentIndex + 1
      }?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#D92D20",
      cancelButtonColor: "#667085",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setEmploymentHistory((prev) =>
        prev.filter((_, index) => index !== employmentIndex),
      );
      setEmploymentUploadedFiles((prev) =>
        prev.filter((_, index) => index !== employmentIndex),
      );

      setErrors((prev) => {
        const updated: Record<string, string> = {};

        Object.keys(prev).forEach((key) => {
          const [indexString, fieldId] = key.split("_");

          const index = Number(indexString);

          if (index < employmentIndex) {
            updated[key] = prev[key];
          } else if (index > employmentIndex) {
            updated[`${index - 1}_${fieldId}`] = prev[key];
          }
        });

        return updated;
      });
    });
  };

  const toggleEmployment = (employmentIndex: number) => {
    setCollapsedEmployments((prev) => ({
      ...prev,
      [employmentIndex]: !prev[employmentIndex],
    }));
  };

  const validateEmploymentHistory = () => {
    const allErrors: Record<string, string> = {};

    let hasError = false;

    employmentHistory.forEach((employment, employmentIndex) => {
      const schema = generateSchema(formFields, employmentIndex);

      // Convert null/undefined values to empty strings before validation.
      const normalizedEmployment = Object.fromEntries(
        Object.entries(employment).map(([fieldId, value]) => [
          fieldId,
          value == null ? "" : value,
        ]),
      );

      const validationResult = schema.safeParse(normalizedEmployment);

      if (!validationResult.success) {
        hasError = true;

        validationResult.error.issues.forEach((issue) => {
          const fieldName = issue.path[0] as string;
          const errorKey = getRecordKey(employmentIndex, fieldName);

          if (!allErrors[errorKey]) {
            allErrors[errorKey] = issue.message;
          }
        });
      }
    });

    setErrors(allErrors);

    if (hasError) {
      const firstErrorKey = Object.keys(allErrors)[0];

      if (firstErrorKey) {
        const [firstIndex] = firstErrorKey.split("_");

        setCollapsedEmployments((prev) => ({
          ...prev,
          [Number(firstIndex)]: false,
        }));
      }

      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please complete all required fields before saving.",
      });

      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const payload = new FormData();

    // =====================================================
    // CREATE JSON DATA WITHOUT FILE / FILE[]
    // =====================================================

    const jsonData = employmentHistory.map((employment) => {
      const record: Record<string, any> = {};

      Object.keys(employment).forEach((fieldId) => {
        const value = employment[fieldId];

        // Single binary file
        if (value instanceof File) {
          return;
        }

        // Multiple binary files
        if (
          Array.isArray(value) &&
          value.some((item) => item instanceof File)
        ) {
          return;
        }

        // Normal field
        record[fieldId] = value ?? "";
      });

      return record;
    });

    // =====================================================
    // APPEND FILES AS BINARY
    // =====================================================

    employmentHistory.forEach((employment, employmentIndex) => {
      Object.keys(employment).forEach((fieldId) => {
        const value = employment[fieldId];

        const fileKey = `employment_${employmentIndex}_${fieldId}`;

        // -----------------------------
        // SINGLE FILE
        // -----------------------------
        if (value instanceof File) {
          payload.append(fileKey, value);
          return;
        }

        // -----------------------------
        // MULTIPLE FILES
        // -----------------------------
        if (Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File) {
              payload.append(fileKey, file);
            }
          });
        }
      });
    });

    // =====================================================
    // NORMAL JSON DATA
    // =====================================================

    payload.append("formData", JSON.stringify(jsonData));

    payload.append("form_type", form_type_new);

    return payload;
  };

  const handleSaveDocument = async () => {
    try {
      if (!isFresher) {
        const isValid = validateEmploymentHistory();

        if (!isValid) {
          return;
        }
      }

      setErrors({});

      const payload = buildPayload();

      const response = await templateSaveAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document saved successfully",
          confirmButtonText: "OK",
        });

        dispatch(triggerPackageReload());

        await employementForm();
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleUpdateDocument = async () => {
    try {
      if (!isFresher) {
        const isValid = validateEmploymentHistory();

        if (!isValid) {
          return;
        }
      }

      setErrors({});

      const payload = buildPayload();

      const response = await templateUpdateAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document update successfully",
          confirmButtonText: "OK",
        });

        // navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleFresherChange = (checked: boolean) => {
    setIsFresher(checked);

    // if (checked) {
    //   setEmploymentHistory([]);
    //   setErrors({});
    //   setNoDocument({});
    //   setCollapsedEmployments({});
    // } else {
    //   setEmploymentHistory([createEmptyEmployment(formFields)]);
    // }
    if (checked) {
      setEmploymentHistory([]);
      setEmploymentUploadedFiles([]);
      setErrors({});
      setNoDocument({});
      setCollapsedEmployments({});
    } else {
      setEmploymentHistory([createEmptyEmployment(formFields)]);
      setEmploymentUploadedFiles([{}]);
    }
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setReplyDocuments(Array.from(e.target.files));
  };

  const removeReplyFile = (index: number) => {
    setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReply = async () => {
    if (!replyRemarks.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
      });

      return;
    }

    try {
      const payload = new FormData();

      payload.append(
        "assignment_template_id",
        assignment_template_id ?? assignmentTemplateId ?? "",
      );

      payload.append("assignment_id", assignment_id ?? assignmentId ?? "");

      payload.append("case_id", case_id ?? caseId ?? "");

      payload.append("candidate_id", candidate_id ?? candidateId ?? "");

      payload.append("actor_due_on", actor_type ?? "CANDIDATE");

      payload.append("remarks", replyRemarks);

      replyDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await insuffResponse.callApi(payload);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your response has been submitted successfully.",
        });

        navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error?.message || "Unable to submit your response.",
      });
    }
  };

  // Section configuration shared by every employment history record.
  const SECTION_CONFIG = [
    {
      title: "Organization Details",
      icon: "ti ti-building",
      fields: [
        "company_name",
        "deployed_company",
        "employment_type",
        "organization_address_line_1",
        "organization_address_line_2",
        "pincode",
        "organization_country",
        "organization_state",
        "organization_city",
      ],
    },
    {
      title: "Employment Details",
      icon: "ti ti-briefcase",
      fields: [
        "employee_code_id",
        "last_position_held",
        "date_of_joining",
        "date_of_relieving",
        "currency",
        "salary_package",
      ],
    },
    {
      title: "Supervisor / Manager Details",
      icon: "ti ti-user",
      fields: [
        "supervisor_details_available",
        "reporting_manager_name",
        "reporting_manager_email",
        "reporting_manager_mobile",
        "reporting_manager_designation",
      ],
    },
    {
      title: "HR Details",
      icon: "ti ti-id-badge-2",
      fields: [
        "hr_details_available",
        "hr_name",
        "hr_contact_details",
        "hr_email_id",
      ],
    },
    {
      title: "Statutory Details",
      icon: "ti ti-file-text",
      fields: ["pf_verification_number", "uan_number"],
    },
  ];

  const sortedNormalFields = formFields
    .filter((field) => field.type !== "file")
    .slice()
    .sort((a, b) => a.display_order - b.display_order);

  // Keep API display_order inside each section; do not drop unknown fields.
  const usedSectionFieldIds = new Set(
    SECTION_CONFIG.flatMap((section) => section.fields),
  );

  const sectionsWithData = SECTION_CONFIG.map((section) => ({
    ...section,
    sectionFields: sortedNormalFields.filter((field) =>
      section.fields.includes(field.field_id),
    ),
  })).filter((section) => section.sectionFields.length > 0);

  const leftoverFields = sortedNormalFields.filter(
    (field) => !usedSectionFieldIds.has(field.field_id),
  );

  if (leftoverFields.length > 0) {
    sectionsWithData.push({
      title: "Other Details",
      icon: "ti ti-list-details",
      fields: [],
      sectionFields: leftoverFields,
    });
  }

  const sortedFileFields = formFields
    .filter((field) => field.type === "file")
    .slice()
    .sort((a, b) => a.display_order - b.display_order);

  const isValidDate = (value: any): value is string => {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-8">
              {formLoading ? (
                <SnipperLoader />
              ) : (
                <div className="card overflow-hidden">
                  {/* Header */}
                  <div
                    className="border-bottom px-4 py-3"
                    style={{
                      backgroundColor: "#FAFAFA",
                    }}
                  >
                    <h5
                      className="fw-semibold mb-1"
                      style={{
                        color: "#101828",
                        fontSize: "17px",
                      }}
                    >
                      {checkDetails?.check_name}
                    </h5>

                    {checkDetails?.check_desc && (
                      <p
                        className="mb-0"
                        style={{
                          color: "#667085",
                          fontSize: "13px",
                          lineHeight: "20px",
                        }}
                      >
                        {checkDetails?.check_desc}
                      </p>
                    )}
                  </div>

                  <div className="p-3">
                    {/* Fresher */}
                    {/* <div
                      className="border rounded-3 p-3 mb-4"
                      style={{
                        background: "#F8F9FA",
                        borderColor: "#DEE2E6",
                      }}
                    >
                      <div className="form-check d-flex align-items-start m-0">
                        <input
                          id="fresher"
                          className="form-check-input mt-1"
                          type="checkbox"
                          checked={isFresher}
                          onChange={(e) =>
                            handleFresherChange(e.target.checked)
                          }
                          disabled={isFormDisabled}
                          style={{
                            width: "20px",
                            height: "20px",
                            cursor: "pointer",
                          }}
                        />

                        <label
                          htmlFor="fresher"
                          className="form-check-label ms-3 w-100"
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="fw-semibold text-dark">
                              I am a Fresher
                            </span>

                            <i className="ti ti-briefcase-off text-primary fs-3"></i>
                          </div>

                          <small className="text-muted">
                            Select this option if you do not have any prior work
                            experience.
                          </small>
                        </label>
                      </div>
                    </div> */}

                    {/* Fresher Message */}
                    {isFresher ? (
                      <div
                        className="border rounded-3 p-4 text-center"
                        style={{
                          background: "#F8FAFC",
                          borderColor: "#E4E7EC",
                        }}
                      >
                        <i
                          className="ti ti-user-check"
                          style={{
                            fontSize: "36px",
                            color: "#0F6E56",
                          }}
                        />

                        <h6 className="fw-semibold mt-2 mb-1">
                          No Employment History
                        </h6>

                        <p className="text-muted small mb-0">
                          Since you are a fresher, no previous employment
                          details are required.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Employment Cards */}
                        {employmentHistory.map(
                          (employment, employmentIndex) => {
                            const isCollapsed =
                              collapsedEmployments[employmentIndex];

                            const companyField = formFields.find(
                              (field) => field.field_id === "company_name",
                            );

                            const joiningField = employment["date_of_joining"];
                            const relievingField =
                              employment["date_of_relieving"];

                            return (
                              <div
                                key={employmentIndex}
                                className="border rounded-3 mb-4 overflow-hidden"
                                style={{
                                  background: "#FFFFFF",
                                  borderColor: "#D0D5DD",
                                }}
                              >
                                {/* Card Header */}
                                <div
                                  className="px-4 py-3 d-flex align-items-center justify-content-between"
                                  style={{
                                    background: "#F8FAFC",
                                    borderBottom: isCollapsed
                                      ? "none"
                                      : "1px solid #EAECF0",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    toggleEmployment(employmentIndex)
                                  }
                                >
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-circle"
                                      style={{
                                        width: "34px",
                                        height: "34px",
                                        background: "#E1F5EE",
                                        color: "#0F6E56",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {employmentIndex + 1}
                                    </div>

                                    <div>
                                      <h6 className="fw-semibold mb-1">
                                        Employment {employmentIndex + 1}
                                      </h6>

                                      <div
                                        className="text-muted small"
                                        style={{
                                          maxWidth: "450px",
                                        }}
                                      >
                                        {companyField
                                          ? employment[companyField.field_id] ||
                                            "Add employment details"
                                          : "Add employment details"}

                                        {joiningField && " - " + joiningField}

                                        {relievingField &&
                                          " to " + relievingField}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center gap-2">
                                    {employmentHistory.length > 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light text-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          removeEmployment(employmentIndex);
                                        }}
                                        disabled={isFormDisabled}
                                      >
                                        <i className="ti ti-trash me-1"></i>
                                        Remove
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        toggleEmployment(employmentIndex);
                                      }}
                                    >
                                      <i
                                        className={`ti ${
                                          isCollapsed
                                            ? "ti-chevron-down"
                                            : "ti-chevron-up"
                                        }`}
                                      ></i>
                                    </button>
                                  </div>
                                </div>

                                {!isCollapsed && (
                                  <div className="p-3">
                                    {/* Dynamic fields grouped into section cards */}
                                    <div className="row">
                                      {sectionsWithData.map((section) => (
                                        <div
                                          key={`${employmentIndex}_${section.title}`}
                                          className="col-12"
                                        >
                                          <div
                                            className="card border-0 shadow-sm"
                                            style={{
                                              borderRadius: "12px",
                                              boxShadow:
                                                "0 1px 6px rgba(0,0,0,0.06)",
                                            }}
                                          >
                                            <div className="card-header bg-white border-0 d-flex align-items-center gap-2 py-3 px-3">
                                              <i
                                                className={`${section.icon} text-primary fs-3`}
                                              ></i>
                                              <h6
                                                className="mb-0 fw-semibold"
                                                style={{
                                                  color: "#344054",
                                                  letterSpacing: "0.02em",
                                                }}
                                              >
                                                {section.title}
                                              </h6>
                                            </div>
                                            <div className="card-body pt-0 px-3">
                                              <div className="row g-3">
                                                {section.sectionFields.map(
                                                  (field) => {
                                                    const errorKey =
                                                      getRecordKey(
                                                        employmentIndex,
                                                        field.field_id,
                                                      );

                                                    const fieldValue =
                                                      employment[
                                                        field.field_id
                                                      ] ?? "";

                                                    return (
                                                      <div
                                                        key={`${employmentIndex}_${field.field_uuid}`}
                                                        className="col-md-6 d-flex flex-column"
                                                      >
                                                        <label className="form-label fw-medium">
                                                          {field.label}

                                                          {field.required && (
                                                            <span className="text-danger ms-1">
                                                              *
                                                            </span>
                                                          )}
                                                        </label>

                                                        {field.type ===
                                                        "dropdown" ? (
                                                          <select
                                                            className={`form-select ${
                                                              errors[errorKey]
                                                                ? "is-invalid"
                                                                : ""
                                                            }`}
                                                            value={
                                                              isFresher
                                                                ? "N/A"
                                                                : fieldValue
                                                            }
                                                            required={
                                                              field.required
                                                            }
                                                            disabled={
                                                              isFormDisabled
                                                            }
                                                            onChange={(e) =>
                                                              handleFieldChange(
                                                                employmentIndex,
                                                                field.field_id,
                                                                e.target.value,
                                                              )
                                                            }
                                                          >
                                                            <option value="">
                                                              {field.placeholder ||
                                                                "Select"}
                                                            </option>

                                                            {field.options?.map(
                                                              (option: any) => (
                                                                <option
                                                                  key={
                                                                    option.value
                                                                  }
                                                                  value={
                                                                    option.value
                                                                  }
                                                                >
                                                                  {option.label}
                                                                </option>
                                                              ),
                                                            )}
                                                          </select>
                                                        ) : (
                                                          <input
                                                            type={field.type}
                                                            className={`form-control ${
                                                              errors[errorKey]
                                                                ? "is-invalid"
                                                                : ""
                                                            }`}
                                                            placeholder={
                                                              field.placeholder
                                                            }
                                                            value={
                                                              isFresher
                                                                ? "N/A"
                                                                : fieldValue
                                                            }
                                                            required={
                                                              field.required
                                                            }
                                                            disabled={
                                                              isFormDisabled
                                                            }
                                                            onChange={(e) =>
                                                              handleFieldChange(
                                                                employmentIndex,
                                                                field.field_id,
                                                                e.target.value,
                                                              )
                                                            }
                                                            min={
                                                              field.field_id ===
                                                                "date_of_relieving" &&
                                                              isValidDate(
                                                                joiningField,
                                                              )
                                                                ? joiningField
                                                                : undefined
                                                            }
                                                            max={
                                                              field.field_id ===
                                                              "date_of_joining"
                                                                ? isValidDate(
                                                                    relievingField,
                                                                  )
                                                                  ? relievingField <
                                                                    getTodayDate()
                                                                    ? relievingField
                                                                    : getTodayDate()
                                                                  : getTodayDate()
                                                                : field.field_id ===
                                                                    "date_of_relieving"
                                                                  ? getYesterdayDate()
                                                                  : undefined
                                                            }
                                                            pattern={
                                                              [
                                                                "uan_number",
                                                                "pf_verification_number",
                                                              ].includes(
                                                                field.field_id,
                                                              )
                                                                ? undefined
                                                                : field
                                                                    ?.validation_rules
                                                                    ?.pattern
                                                            }
                                                            maxLength={
                                                              field.field_id ===
                                                              "uan_number"
                                                                ? 12
                                                                : field.field_id ===
                                                                    "pf_verification_number"
                                                                  ? 26
                                                                  : field
                                                                      ?.validation_rules
                                                                      ?.maxLength
                                                            }
                                                            minLength={
                                                              [
                                                                "uan_number",
                                                                "pf_verification_number",
                                                              ].includes(
                                                                field.field_id,
                                                              )
                                                                ? undefined
                                                                : field
                                                                    ?.validation_rules
                                                                    ?.minLength
                                                            }
                                                            inputMode={
                                                              field.field_id ===
                                                              "uan_number"
                                                                ? "numeric"
                                                                : undefined
                                                            }
                                                            onBlur={(e) => {
                                                              const message =
                                                                getStatutoryError(
                                                                  field.field_id,
                                                                  e.target
                                                                    .value,
                                                                );
                                                              if (
                                                                [
                                                                  "uan_number",
                                                                  "pf_verification_number",
                                                                ].includes(
                                                                  field.field_id,
                                                                )
                                                              ) {
                                                                setErrors(
                                                                  (prev) => ({
                                                                    ...prev,
                                                                    [errorKey]:
                                                                      message,
                                                                  }),
                                                                );
                                                              }
                                                            }}
                                                          />
                                                        )}

                                                        {errors[errorKey] && (
                                                          <div className="invalid-feedback d-block">
                                                            {errors[errorKey]}
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  },
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Documents */}
                                    {sortedFileFields.length > 0 && (
                                      <div className="border-top pt-4 mt-2">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                          <div>
                                            <h6 className="fw-bold mb-1">
                                              Employment Documents
                                            </h6>

                                            <small className="text-muted">
                                              Upload documents for Employment{" "}
                                              {employmentIndex + 1}
                                            </small>
                                          </div>
                                        </div>

                                        <div
                                          className="rounded-3 p-3 mb-4"
                                          style={{
                                            background: "#F8FAFC",
                                            border: "1px solid #EAECF0",
                                          }}
                                        >
                                          <small className="text-muted">
                                            <strong>Instructions:</strong>
                                          </small>

                                          <ul className="small text-muted mb-0 mt-2 ps-3">
                                            <li>
                                              Kindly ensure the document is
                                              readable and not pixelated or
                                              blurred.
                                            </li>
                                            <li>
                                              Accepted formats: PDF, JPEG, JPG,
                                              PNG, DOC, DOCX.
                                            </li>
                                            <li>
                                              Document size should not exceed
                                              5MB.
                                            </li>
                                          </ul>
                                        </div>

                                        {sortedFileFields.map((field) => {
                                          const key = getRecordKey(
                                            employmentIndex,
                                            field.field_id,
                                          );

                                          // const selectedFile =
                                          //   employment[field.field_id];

                                          // const isNoDocument = noDocument[key];

                                          // const uploadedFile =
                                          //   field.uploaded_files?.[0];
                                          const selectedFile =
                                            employment[field.field_id];

                                          const isNoDocument = noDocument[key];

                                          const uploadedFiles =
                                            employmentUploadedFiles[
                                              employmentIndex
                                            ]?.[field.field_id] || [];

                                          return (
                                            <div
                                              key={`${employmentIndex}_${field.field_uuid}`}
                                              className="row align-items-center mb-4 pb-4 border-bottom"
                                            >
                                              <div className="col-lg-7">
                                                <label className="form-label fw-semibold">
                                                  {field.label}

                                                  {field.required && (
                                                    <span className="text-danger ms-1">
                                                      *
                                                    </span>
                                                  )}
                                                </label>

                                                <input
                                                  ref={(el) => {
                                                    fileInputRefs.current[key] =
                                                      el;
                                                  }}
                                                  type="file"
                                                  className={`form-control ${
                                                    errors[key]
                                                      ? "is-invalid"
                                                      : ""
                                                  }`}
                                                  multiple={
                                                    field.file_config
                                                      ?.multiple === true
                                                  }
                                                  accept={field.file_config?.allowedTypes
                                                    ?.map(
                                                      (type: string) =>
                                                        `.${type}`,
                                                    )
                                                    .join(",")}
                                                  onChange={(e) => {
                                                    if (
                                                      field.file_config
                                                        ?.multiple
                                                    ) {
                                                      handleFieldChange(
                                                        employmentIndex,
                                                        field.field_id,
                                                        Array.from(
                                                          e.target.files || [],
                                                        ),
                                                      );
                                                    } else {
                                                      handleFieldChange(
                                                        employmentIndex,
                                                        field.field_id,
                                                        e.target.files?.[0] ||
                                                          null,
                                                      );
                                                    }
                                                  }}
                                                  disabled={
                                                    isNoDocument ||
                                                    isFormDisabled
                                                  }
                                                />

                                                {errors[key] && (
                                                  <div className="invalid-feedback d-block">
                                                    {errors[key]}
                                                  </div>
                                                )}

                                                <div className="form-check mt-2">
                                                  <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={
                                                      isNoDocument || false
                                                    }
                                                    onChange={(e) =>
                                                      handleNoDocumentChange(
                                                        employmentIndex,
                                                        field.field_id,
                                                        e.target.checked,
                                                      )
                                                    }
                                                    disabled={isFormDisabled}
                                                  />

                                                  <label className="form-check-label text-muted small">
                                                    I do not have this document
                                                  </label>
                                                </div>
                                              </div>

                                              <div className="col-lg-5 text-center mt-3 mt-lg-0">
                                                {/* ========================================== */}
                                                {/* NEWLY SELECTED MULTIPLE FILES */}
                                                {/* ========================================== */}

                                                {Array.isArray(selectedFile) &&
                                                selectedFile.length > 0 ? (
                                                  <>
                                                    <i
                                                      className="bi bi-file-earmark-pdf-fill text-danger"
                                                      style={{
                                                        fontSize: "50px",
                                                        cursor: "pointer",
                                                      }}
                                                    />

                                                    {selectedFile.map(
                                                      (
                                                        file: File,
                                                        index: number,
                                                      ) => (
                                                        <p
                                                          key={`${file.name}-${index}`}
                                                          className="text-danger small mb-0 text-truncate"
                                                          title={file.name}
                                                        >
                                                          {file.name}
                                                        </p>
                                                      ),
                                                    )}
                                                  </>
                                                ) : selectedFile instanceof
                                                  File ? (
                                                  /* ========================================== */
                                                  /* NEWLY SELECTED SINGLE FILE */
                                                  /* ========================================== */

                                                  <>
                                                    <i
                                                      className="bi bi-file-earmark-pdf-fill text-danger"
                                                      style={{
                                                        fontSize: "50px",
                                                        cursor: "pointer",
                                                      }}
                                                    />

                                                    <p
                                                      className="text-danger small mb-0 text-truncate"
                                                      title={selectedFile.name}
                                                    >
                                                      {selectedFile.name}
                                                    </p>
                                                  </>
                                                ) : uploadedFiles.length > 0 ? (
                                                  /* ========================================== */
                                                  /* EXISTING UPLOADED FILES */
                                                  /* ========================================== */

                                                  <>
                                                    <i
                                                      className="bi bi-file-earmark-pdf-fill text-danger"
                                                      style={{
                                                        fontSize: "48px",
                                                      }}
                                                    />

                                                    {uploadedFiles.map(
                                                      (
                                                        file: any,
                                                        index: number,
                                                      ) => (
                                                        <a
                                                          key={
                                                            file.id ??
                                                            `${file.original_name}-${index}`
                                                          }
                                                          href={`${apibaseurl}${file.url}`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-decoration-none d-block"
                                                        >
                                                          <p
                                                            className="text-danger small mb-0 text-truncate"
                                                            title={
                                                              file.original_name ||
                                                              file.name
                                                            }
                                                          >
                                                            {file.original_name ||
                                                              file.name ||
                                                              `Document ${index + 1}`}
                                                          </p>
                                                        </a>
                                                      ),
                                                    )}
                                                  </>
                                                ) : (
                                                  /* ========================================== */
                                                  /* NO FILE */
                                                  /* ========================================== */

                                                  <>
                                                    <i
                                                      className="bi bi-file-earmark-pdf-fill text-danger"
                                                      style={{
                                                        fontSize: "48px",
                                                      }}
                                                    />

                                                    <p className="text-muted small mb-0">
                                                      No File Selected
                                                    </p>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}

                        {/* Add Employment */}
                        <button
                          type="button"
                          className="w-100 mb-4"
                          onClick={addEmployment}
                          disabled={isFormDisabled}
                          style={{
                            border: "1px dashed #98A2B3",
                            background: "#F8FAFC",
                            borderRadius: "10px",
                            padding: "14px 20px",
                            cursor: isFormDisabled ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            if (!isFormDisabled) {
                              e.currentTarget.style.background = "#F0FDF9";
                              e.currentTarget.style.borderColor = "#0F6E56";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#F8FAFC";
                            e.currentTarget.style.borderColor = "#98A2B3";
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                width: "34px",
                                height: "34px",
                                background: "#E1F5EE",
                                color: "#0F6E56",
                                flexShrink: 0,
                              }}
                            >
                              <i
                                className="ti ti-plus"
                                style={{ fontSize: "18px" }}
                              ></i>
                            </div>

                            <div className="text-start">
                              <div
                                className="fw-semibold"
                                style={{
                                  color: "#344054",
                                  fontSize: "14px",
                                }}
                              >
                                Add Previous Employment
                              </div>

                              <div
                                style={{
                                  color: "#667085",
                                  fontSize: "12px",
                                  marginTop: "2px",
                                }}
                              >
                                Add details of another company
                              </div>
                            </div>
                          </div>
                        </button>
                      </>
                    )}

                    {/* Save */}
                    {!isFormDisabled &&
                      templateStatus !== "CANDIDATE_INSUFF" && (
                        <div className="d-flex gap-2 align-items-center mt-4">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveDocument}
                            disabled={templateSaveAPI.loading}
                          >
                            {templateSaveAPI.loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="ti ti-device-floppy me-2"></i>
                                Save
                              </>
                            )}
                          </button>
                        </div>
                      )}

                    {/* Insufficiency Update */}
                    {templateStatus === "CANDIDATE_INSUFF" && (
                      <div className="d-flex gap-2 align-items-center mt-4">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleUpdateDocument}
                          disabled={templateUpdateAPI.loading}
                        >
                          {templateUpdateAPI.loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Updating...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-upload me-2"></i>
                              Save Form & Documents
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => setShowReplyModal(true)}
                        >
                          <i className="ti ti-message-circle me-2"></i>
                          Reply to Insufficiency
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-4">
              <div
                style={{
                  position: "sticky",
                  top: "60px",
                  alignSelf: "flex-start",
                }}
              >
                <div className="card border-0 shadow-sm">
                  {/* Header */}
                  <div
                    className="text-center py-4"
                    style={{ backgroundColor: "#f7f1e3" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                      style={{ width: "70px", height: "70px" }}
                    >
                      {/* <i
                                  className="bi bi-lightbulb-fill text-warning"
                                  style={{ fontSize: "35px" }}
                                ></i> */}
                      <img src={bulbImage} alt="buld" />
                    </div>

                    <h6 className="fw-bold mb-0">Instructions</h6>
                  </div>

                  {/* Body */}
                  <div className="card-body">
                    {/* Instruction 1 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>

                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 1</h6>

                        <p className="text-muted mb-0">
                          Please provide your relevant employment details as
                          mentioned on your official document (Experience
                          Letter, Relieving Letter, Offer Letter) in the form.
                          Please start with your previous employer prior to
                          joining (Teleperformance Global Services Private
                          Limited).{" "}
                        </p>
                      </div>
                    </div>

                    {/* Instruction 2 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 2</h6>

                        <p className="text-muted mb-0">
                          You may be required to attach a copy of the Employment
                          Documents (Appointment Letter, Relieving Letter,
                          Experience Letter). Kindly ensure the
                          photocopy/picture is readable, and is not
                          cut/pixelated/blurred. The required document set will
                          be displayed in the upload documents sections.
                        </p>
                      </div>
                    </div>
                    {/* Instruction 2 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 3</h6>

                        <p className="text-muted mb-0">
                          Note: We will reach out to you immediate/previous
                          employer, once you leave the company and provide us
                          the relevant documentation to proceed ahead with the
                          verification.
                        </p>
                      </div>
                    </div>
                    {/* Instruction 2 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 4</h6>

                        <p className="text-muted mb-0">
                          In case we are unable to obtain verification from your
                          HR/Supervisor due to non-responsiveness, we can
                          utilise the data from your Form 16 to validate your
                          employment .{" "}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 5</h6>

                        <p className="text-muted mb-0">
                          It is recommended to provide form 16 for any
                          assessment year pertaining to the employer’s
                          details.{" "}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reply to Insufficiency Modal */}
          {showReplyModal && (
            <div
              className="modal fade show d-block"
              style={{
                background: "rgba(0,0,0,.5)",
              }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title fw-bold mb-1">
                        Respond to Insufficiency
                      </h5>

                      <small className="text-muted">
                        Please provide your explanation and upload supporting
                        documents if applicable.
                      </small>
                    </div>

                    <button
                      className="btn-close"
                      onClick={() => setShowReplyModal(false)}
                    />
                  </div>

                  <div className="modal-body">
                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Remarks
                        <span className="text-danger ms-1">*</span>
                      </label>

                      <textarea
                        rows={5}
                        className="form-control"
                        placeholder="Enter your response to the verifier..."
                        value={replyRemarks}
                        onChange={(e) => setReplyRemarks(e.target.value)}
                        style={{
                          resize: "none",
                        }}
                      />

                      <small className="text-muted">
                        Clearly explain the requested information or
                        clarification.
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Supporting Documents
                      </label>

                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleReplyFileChange}
                      />

                      <small className="text-muted">
                        Accepted formats: JPG, JPEG, PNG, PDF. Maximum 10 MB per
                        file.
                      </small>
                    </div>

                    {replyDocuments.length > 0 && (
                      <div className="mt-3">
                        <label className="fw-semibold mb-2">
                          Selected Files
                        </label>

                        <div className="d-flex flex-wrap gap-2">
                          {replyDocuments.map((file, index) => (
                            <span
                              key={index}
                              className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
                            >
                              <i className="ti ti-paperclip me-2"></i>

                              <span
                                className="text-truncate"
                                style={{
                                  maxWidth: 180,
                                }}
                              >
                                {file.name}
                              </span>

                              <button
                                type="button"
                                className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
                                onClick={() => removeReplyFile(index)}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer gap-3">
                    <button
                      className="btn btn-light border"
                      onClick={() => setShowReplyModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitReply}
                      disabled={insuffResponse.loading}
                    >
                      {insuffResponse.loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send me-2"></i>
                          Submit Response
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};

export default EmployementHistory;

// import React, { useEffect, useRef, useState } from "react";
// import "react-datepicker/dist/react-datepicker.css";
// import Footer from "../../../core/common/footer/Footer";
// import { useDispatch, useSelector } from "react-redux";
// import { RootState } from "../../../core/data/redux/store";
// import z from "zod";
// import useApi from "../../../core/customhook/useApi";
// import Swal from "sweetalert2";
// import SnipperLoader from "../../../core/common/loader/SnipperLoader";
// import { apibaseurl } from "../../../environment";
// import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
// import { all_routes } from "../../router/all_routes";
// import { useLocation, useNavigate } from "react-router";
// import { useAuth } from "../../auth/AuthContext";
// import bulbImage from "../../../images/buld.png";
// import {
//   getTodayDate,
//   getYesterdayDate,
// } from "../../../core/common/DateValidHelper";

// type EmploymentRecord = Record<string, any>;

// const EmployementHistory = () => {
//   const templates = useSelector((state: RootState) => state.package.templates);

//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { state } = useLocation();
//   const { authData } = useAuth();

//   const {
//     assignment_template_id,
//     assignment_id,
//     case_id,
//     candidate_id,
//     actor_type,
//   } = state || {};

//   const EMPLOYMENT_CHECK_NAMES = [
//     "Employment - 2 Year",
//     "Employment - 3 Year",
//     "Employment - 5 Year",
//     "Employment - 7 Year",
//     "Employment - 10 Year",
//   ];

//   const employmentTemplate = templates.find((item: any) =>
//     EMPLOYMENT_CHECK_NAMES.includes(item.check_name),
//   );

//   const assignmentTemplateId = employmentTemplate?.assignment_template_id;

//   const caseId = employmentTemplate?.case_id;
//   const assignmentId = employmentTemplate?.assignment_id;
//   const candidateId = authData?.candidate?.candidateId;
//   const templateStatus = employmentTemplate?.status;

//   const [formFields, setFormFields] = useState<any[]>([]);
//   const [checkDetails, setCheckDetails] = useState<any>();

//   const [employmentHistory, setEmploymentHistory] = useState<
//     EmploymentRecord[]
//   >([]);

//   const [employmentUploadedFiles, setEmploymentUploadedFiles] = useState<
//     Record<string, any>[]
//   >([]);

//   const [noDocument, setNoDocument] = useState<Record<string, boolean>>({});

//   const [isFresher, setIsFresher] = useState(false);

//   const [errors, setErrors] = useState<Record<string, string>>({});

//   const [collapsedEmployments, setCollapsedEmployments] = useState<
//     Record<number, boolean>
//   >({});

//   const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

//   const [showReplyModal, setShowReplyModal] = useState(false);
//   const [replyRemarks, setReplyRemarks] = useState("");
//   const [replyDocuments, setReplyDocuments] = useState<File[]>([]);

//   const disabledStatuses = [
//     "CANDIDATE_SUBMITTED",
//     "VERIFICATION_IN_PROGRESS",
//     "VERIFIED",
//     "STOPPED",
//     "OUT_OF_SCOPE",
//     "CANCELLED",
//     "NA",
//   ];

//   const isFormDisabled = disabledStatuses.includes(templateStatus ?? "");
//   const form_type_new = formFields?.[0]?.form_type_new;

//   const createEmptyEmployment = (fields: any[]): EmploymentRecord => {
//     const record: EmploymentRecord = {};

//     fields.forEach((field: any) => {
//       record[field.field_id] = null;
//     });

//     return record;
//   };
//   const getRecordKey = (employmentIndex: number, fieldId: string) =>
//     `${employmentIndex}_${fieldId}`;

//   const generateSchema = (fields: any[], employmentIndex: number) => {
//     const schemaFields: Record<string, z.ZodTypeAny> = {};

//     fields.forEach((field) => {
//       let validator: z.ZodTypeAny = z.any();

//       if (field.type === "file") {
//         const key = getRecordKey(employmentIndex, field.field_id);

//         const skipValidation = noDocument?.[key] === true;
//         const isMultiple = field.file_config?.multiple === true;

//         const existingFiles =
//           employmentUploadedFiles[employmentIndex]?.[field.field_id];

//         const hasExistingFiles =
//           Array.isArray(existingFiles) && existingFiles.length > 0;

//         if (skipValidation || hasExistingFiles) {
//           validator = z.any().optional();
//         } else if (isMultiple) {
//           validator = field.required
//             ? z.array(z.instanceof(File)).min(1, `${field.label} is required`)
//             : z.array(z.instanceof(File)).optional();
//         } else {
//           validator = field.required
//             ? z.instanceof(File, {
//                 message: `${field.label} is required`,
//               })
//             : z.any().optional();
//         }
//       } else {
//         // Keep string validation separate to preserve TypeScript types
//         let stringValidator: z.ZodString = z.string();

//         if (field.required) {
//           stringValidator = stringValidator.min(
//             1,
//             `${field.label} is required`,
//           );
//         }

//         if (field.validation_rules?.minLength) {
//           stringValidator = stringValidator.min(
//             field.validation_rules.minLength,
//             `${field.label} must be at least ${field.validation_rules.minLength} characters`,
//           );
//         }

//         if (field.validation_rules?.maxLength) {
//           stringValidator = stringValidator.max(
//             field.validation_rules.maxLength,
//             `${field.label} cannot exceed ${field.validation_rules.maxLength} characters`,
//           );
//         }

//         if (field.validation_rules?.pattern) {
//           stringValidator = stringValidator.regex(
//             new RegExp(field.validation_rules.pattern),
//             `${field.label} is invalid`,
//           );
//         }

//         validator = field.required ? stringValidator : z.string().optional();
//       }

//       schemaFields[field.field_id] = validator;
//     });

//     return z.object(schemaFields).superRefine((data, ctx) => {
//       const joining = data.date_of_joining;
//       const relieving = data.date_of_relieving;

//       // 1. Joining Date cannot be in the future
//       if (isValidDate(joining) && joining > getTodayDate()) {
//         ctx.addIssue({
//           code: z.ZodIssueCode.custom,
//           path: ["date_of_joining"],
//           message: "Date of Joining cannot be a future date",
//         });
//       }

//       // 2. Relieving Date must be before today
//       if (isValidDate(relieving) && relieving >= getTodayDate()) {
//         ctx.addIssue({
//           code: z.ZodIssueCode.custom,
//           path: ["date_of_relieving"],
//           message: "Date of Relieving must be before today",
//         });
//       }

//       // 3. Relieving Date cannot be before Joining Date
//       if (
//         isValidDate(joining) &&
//         isValidDate(relieving) &&
//         relieving < joining
//       ) {
//         ctx.addIssue({
//           code: z.ZodIssueCode.custom,
//           path: ["date_of_relieving"],
//           message: "Date of Relieving cannot be before Date of Joining",
//         });
//       }
//     });

//   };

//   const formFieldsAPI = useApi<any>(
//     `/api/v1/candidate/templates/${assignmentTemplateId}`,
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const templateSaveAPI = useApi<any>(
//     `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
//     {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const templateUpdateAPI = useApi<any>(
//     `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
//     {
//       method: "PUT",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const insuffResponse = useApi<any>(
//     `/api/v1/insufficiency/candidate-response`,
//     {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const employementForm = async () => {
//     try {
//       const response = await formFieldsAPI.callApi();

//       if (response?.success) {
//         const checkdetails = response?.data?.check;

//         handleEmploymentResponse(response);

//         setCheckDetails(checkdetails);
//         setErrors({});
//         setCollapsedEmployments({});
//         setNoDocument({});
//       }
//     } catch (error) {
//       console.error("Error fetching form fields:", error);
//     }
//   };

//   const formLoading = formFieldsAPI.loading;

//   useEffect(() => {
//     if (assignmentTemplateId) {
//       employementForm();
//     }
//   }, [assignmentTemplateId]);

//   const handleEmploymentResponse = (response: any) => {
//     const fields = response?.data?.fields || [];

//     if (!fields.length) {
//       setFormFields([]);
//       setEmploymentHistory([]);
//       setEmploymentUploadedFiles([]);
//       return;
//     }

//     // AFTER SAVE / EXISTING EMPLOYMENT RECORDS
//     if (fields[0]?.fields && Array.isArray(fields[0].fields)) {
//       setFormFields(fields[0].fields);

//       const employmentRecords = fields.map((entry: any) => {
//         const record: Record<string, any> = {};

//         entry.fields.forEach((field: any) => {
//           record[field.field_id] = field.value ?? null;
//         });

//         return record;
//       });

//       // Store uploaded files separately for each employment record
//       const uploadedFilesByEmployment = fields.map((entry: any) => {
//         const files: Record<string, any> = {};

//         entry.fields.forEach((field: any) => {
//           if (
//             Array.isArray(field.uploaded_files) &&
//             field.uploaded_files.length > 0
//           ) {
//             files[field.field_id] = field.uploaded_files;
//           }
//         });

//         return files;
//       });

//       setEmploymentHistory(employmentRecords);
//       setEmploymentUploadedFiles(uploadedFilesByEmployment);

//       return;
//     }

//     // FIRST TIME
//     setFormFields(fields);

//     setEmploymentHistory([createEmptyEmployment(fields)]);

//     // No uploaded files for a new/blank employment
//     setEmploymentUploadedFiles([{}]);
//   };

//   const handleFieldChange = async (
//     employmentIndex: number,
//     fieldId: string,
//     value: any,
//   ) => {
//     // Date validation for the current employment record
//     if (fieldId === "date_of_joining" || fieldId === "date_of_relieving") {
//       const currentEmployment = employmentHistory[employmentIndex] || {};

//       const joining =
//         fieldId === "date_of_joining"
//           ? value
//           : currentEmployment.date_of_joining;

//       const relieving =
//         fieldId === "date_of_relieving"
//           ? value
//           : currentEmployment.date_of_relieving;

//       const errorKey = getRecordKey(employmentIndex, fieldId);

//       let dateError = "";

//       if (
//         fieldId === "date_of_joining" &&
//         isValidDate(value) &&
//         value > getTodayDate()
//       ) {
//         dateError = "Date of Joining cannot be a future date";
//       } else if (
//         fieldId === "date_of_relieving" &&
//         isValidDate(value) &&
//         value >= getTodayDate()
//       ) {
//         dateError = "Date of Relieving must be before today";
//       } else if (
//         isValidDate(joining) &&
//         isValidDate(relieving) &&
//         relieving < joining
//       ) {
//         dateError =
//           fieldId === "date_of_relieving"
//             ? "Date of Relieving cannot be before Date of Joining"
//             : "Date of Joining cannot be after Date of Relieving";
//       }

//       if (dateError) {
//         setEmploymentHistory((prev) =>
//           prev.map((record, index) =>
//             index === employmentIndex ? { ...record, [fieldId]: "" } : record,
//           ),
//         );

//         setErrors((prev) => ({
//           ...prev,
//           [errorKey]: dateError,
//         }));

//         return;
//       }
//     }

//     // KEEP YOUR EXISTING CODE BELOW UNCHANGED

//     setEmploymentHistory((prev) => {
//       const updated = [...prev];

//       const currentRecord = {
//         ...(updated[employmentIndex] || {}),
//       };

//       currentRecord[fieldId] = value;

//       // Supervisor Details
//       if (fieldId === "supervisor_details_available") {
//         if (value === "NO") {
//           currentRecord.reporting_manager_name = "N/A";
//           currentRecord.reporting_manager_email = "N/A";
//           currentRecord.reporting_manager_mobile = "N/A";
//           currentRecord.reporting_manager_designation = "N/A";
//         } else {
//           currentRecord.reporting_manager_name = "";
//           currentRecord.reporting_manager_email = "";
//           currentRecord.reporting_manager_mobile = "";
//           currentRecord.reporting_manager_designation = "";
//         }
//       }

//       // HR Details
//       if (fieldId === "hr_details_available") {
//         if (value === "NO") {
//           currentRecord.hr_name = "N/A";
//           currentRecord.hr_contact_details = "N/A";
//           currentRecord.hr_email_id = "N/A";
//         } else {
//           currentRecord.hr_name = "";
//           currentRecord.hr_contact_details = "";
//           currentRecord.hr_email_id = "";
//         }
//       }

//       // Pincode
//       if (fieldId === "pincode" && String(value).length < 6) {
//         currentRecord.organization_state = "";
//         currentRecord.organization_city = "";
//       }

//       updated[employmentIndex] = currentRecord;

//       return updated;
//     });

//     const errorKey = getRecordKey(employmentIndex, fieldId);

//     setErrors((prev) => ({
//       ...prev,
//       [errorKey]: "",
//     }));

//     if (fieldId === "pincode" && /^[0-9]{6}$/.test(String(value))) {
//       await getLocationByPincode(employmentIndex, value);
//     }
//   };

//   const handleNoDocumentChange = (
//     employmentIndex: number,
//     fieldId: string,
//     checked: boolean,
//   ) => {
//     const key = getRecordKey(employmentIndex, fieldId);

//     setNoDocument((prev) => ({
//       ...prev,
//       [key]: checked,
//     }));

//     if (checked) {
//       handleFieldChange(employmentIndex, fieldId, null);

//       if (fileInputRefs.current[key]) {
//         fileInputRefs.current[key]!.value = "";
//       }

//       setErrors((prev) => ({
//         ...prev,
//         [key]: "",
//       }));
//     }
//   };

//   const getLocationByPincode = async (
//     employmentIndex: number,
//     pincode: string,
//   ) => {
//     try {
//       const response = await fetch(
//         `https://api.postalpincode.in/pincode/${pincode}`,
//       );

//       const data = await response.json();

//       if (
//         data?.[0]?.Status === "Success" &&
//         data?.[0]?.PostOffice?.length > 0
//       ) {
//         const postOffice = data[0].PostOffice[0];

//         setEmploymentHistory((prev) => {
//           const updated = [...prev];

//           updated[employmentIndex] = {
//             ...(updated[employmentIndex] || {}),
//             organization_country: postOffice.Country || "India",
//             organization_state: postOffice.State || "",
//             organization_city:
//               postOffice.District ||
//               postOffice.Division ||
//               postOffice.Block ||
//               "",
//           };

//           return updated;
//         });

//         setErrors((prev) => ({
//           ...prev,
//           [getRecordKey(employmentIndex, "pincode")]: "",
//           [getRecordKey(employmentIndex, "organization_country")]: "",
//           [getRecordKey(employmentIndex, "organization_state")]: "",
//           [getRecordKey(employmentIndex, "organization_city")]: "",
//         }));
//       } else {
//         setEmploymentHistory((prev) => {
//           const updated = [...prev];

//           updated[employmentIndex] = {
//             ...(updated[employmentIndex] || {}),
//             organization_country: "",
//             organization_state: "",
//             organization_city: "",
//           };

//           return updated;
//         });
//       }
//     } catch (error) {
//       console.error("Pincode API error:", error);
//     }
//   };

//   // const addEmployment = () => {
//   //   const newRecord = createEmptyEmployment(formFields);

//   //   setEmploymentHistory((prev) => [...prev, newRecord]);

//   //   setCollapsedEmployments((prev) => ({
//   //     ...prev,
//   //     [prev ? employmentHistory.length : 0]: false,
//   //   }));
//   // };
//   const addEmployment = () => {
//     const newRecord = createEmptyEmployment(formFields);

//     setEmploymentHistory((prev) => [...prev, newRecord]);

//     // New employment has no uploaded files
//     setEmploymentUploadedFiles((prev) => [...prev, {}]);

//     setCollapsedEmployments((prev) => ({
//       ...prev,
//       [employmentHistory.length]: false,
//     }));
//   };
//   const removeEmployment = (employmentIndex: number) => {
//     if (employmentHistory.length === 1) {
//       Swal.fire({
//         icon: "warning",
//         title: "Cannot Remove",
//         text: "At least one employment record is required.",
//       });

//       return;
//     }

//     Swal.fire({
//       icon: "warning",
//       title: "Remove Employment?",
//       text: `Are you sure you want to remove Employment ${
//         employmentIndex + 1
//       }?`,
//       showCancelButton: true,
//       confirmButtonText: "Yes, Remove",
//       cancelButtonText: "Cancel",
//       confirmButtonColor: "#D92D20",
//       cancelButtonColor: "#667085",
//       reverseButtons: true,
//     }).then((result) => {
//       if (!result.isConfirmed) return;

//       setEmploymentHistory((prev) =>
//         prev.filter((_, index) => index !== employmentIndex),
//       );
//       setEmploymentUploadedFiles((prev) =>
//         prev.filter((_, index) => index !== employmentIndex),
//       );

//       setErrors((prev) => {
//         const updated: Record<string, string> = {};

//         Object.keys(prev).forEach((key) => {
//           const [indexString, fieldId] = key.split("_");

//           const index = Number(indexString);

//           if (index < employmentIndex) {
//             updated[key] = prev[key];
//           } else if (index > employmentIndex) {
//             updated[`${index - 1}_${fieldId}`] = prev[key];
//           }
//         });

//         return updated;
//       });
//     });
//   };

//   const toggleEmployment = (employmentIndex: number) => {
//     setCollapsedEmployments((prev) => ({
//       ...prev,
//       [employmentIndex]: !prev[employmentIndex],
//     }));
//   };

//   const validateEmploymentHistory = () => {
//     const allErrors: Record<string, string> = {};

//     let hasError = false;

//     employmentHistory.forEach((employment, employmentIndex) => {
//       const schema = generateSchema(formFields, employmentIndex);

//       // Convert null/undefined values to empty strings before validation.
//       const normalizedEmployment = Object.fromEntries(
//         Object.entries(employment).map(([fieldId, value]) => [
//           fieldId,
//           value == null ? "" : value,
//         ]),
//       );

//       const validationResult = schema.safeParse(normalizedEmployment);

//       if (!validationResult.success) {
//         hasError = true;

//         validationResult.error.issues.forEach((issue) => {
//           const fieldName = issue.path[0] as string;
//           const errorKey = getRecordKey(employmentIndex, fieldName);

//           if (!allErrors[errorKey]) {
//             allErrors[errorKey] = issue.message;
//           }
//         });
//       }
//     });

//     setErrors(allErrors);

//     if (hasError) {
//       const firstErrorKey = Object.keys(allErrors)[0];

//       if (firstErrorKey) {
//         const [firstIndex] = firstErrorKey.split("_");

//         setCollapsedEmployments((prev) => ({
//           ...prev,
//           [Number(firstIndex)]: false,
//         }));
//       }

//       Swal.fire({
//         icon: "warning",
//         title: "Warning",
//         text: "Please complete all required fields before saving.",
//       });

//       return false;
//     }

//     return true;
//   };

//   const buildPayload = () => {
//     const payload = new FormData();

//     // =====================================================
//     // CREATE JSON DATA WITHOUT FILE / FILE[]
//     // =====================================================

//     const jsonData = employmentHistory.map((employment) => {
//       const record: Record<string, any> = {};

//       Object.keys(employment).forEach((fieldId) => {
//         const value = employment[fieldId];

//         // Single binary file
//         if (value instanceof File) {
//           return;
//         }

//         // Multiple binary files
//         if (
//           Array.isArray(value) &&
//           value.some((item) => item instanceof File)
//         ) {
//           return;
//         }

//         // Normal field
//         record[fieldId] = value ?? "";
//       });

//       return record;
//     });

//     // =====================================================
//     // APPEND FILES AS BINARY
//     // =====================================================

//     employmentHistory.forEach((employment, employmentIndex) => {
//       Object.keys(employment).forEach((fieldId) => {
//         const value = employment[fieldId];

//         const fileKey = `employment_${employmentIndex}_${fieldId}`;

//         // -----------------------------
//         // SINGLE FILE
//         // -----------------------------
//         if (value instanceof File) {
//           payload.append(fileKey, value);
//           return;
//         }

//         // -----------------------------
//         // MULTIPLE FILES
//         // -----------------------------
//         if (Array.isArray(value)) {
//           value.forEach((file) => {
//             if (file instanceof File) {
//               payload.append(fileKey, file);
//             }
//           });
//         }
//       });
//     });

//     // =====================================================
//     // NORMAL JSON DATA
//     // =====================================================

//     payload.append("formData", JSON.stringify(jsonData));

//     payload.append("form_type", form_type_new);

//     return payload;
//   };

//   const handleSaveDocument = async () => {
//     try {
//       if (!isFresher) {
//         const isValid = validateEmploymentHistory();

//         if (!isValid) {
//           return;
//         }
//       }

//       setErrors({});

//       const payload = buildPayload();

//       const response = await templateSaveAPI.callApi(payload);

//       if (response?.success) {
//         Swal.fire({
//           icon: "success",
//           title: "Success",
//           text: response?.message || "Document saved successfully",
//           confirmButtonText: "OK",
//         });

//         dispatch(triggerPackageReload());

//         await employementForm();
//       }
//     } catch (error: any) {
//       Swal.fire({
//         icon: "error",
//         title: "Save Failed",
//         text:
//           error?.response?.data?.error ||
//           error?.message ||
//           "Something went wrong",
//       });
//     }
//   };

//   const handleUpdateDocument = async () => {
//     try {
//       if (!isFresher) {
//         const isValid = validateEmploymentHistory();

//         if (!isValid) {
//           return;
//         }
//       }

//       setErrors({});

//       const payload = buildPayload();

//       const response = await templateUpdateAPI.callApi(payload);

//       if (response?.success) {
//         Swal.fire({
//           icon: "success",
//           title: "Success",
//           text: response?.message || "Document update successfully",
//           confirmButtonText: "OK",
//         });

//         // navigate(all_routes.caseSummary);
//       }
//     } catch (error: any) {
//       Swal.fire({
//         icon: "error",
//         title: "Update Failed",
//         text:
//           error?.response?.data?.error ||
//           error?.message ||
//           "Something went wrong",
//       });
//     }
//   };

//   const handleFresherChange = (checked: boolean) => {
//     setIsFresher(checked);

//     // if (checked) {
//     //   setEmploymentHistory([]);
//     //   setErrors({});
//     //   setNoDocument({});
//     //   setCollapsedEmployments({});
//     // } else {
//     //   setEmploymentHistory([createEmptyEmployment(formFields)]);
//     // }
//     if (checked) {
//       setEmploymentHistory([]);
//       setEmploymentUploadedFiles([]);
//       setErrors({});
//       setNoDocument({});
//       setCollapsedEmployments({});
//     } else {
//       setEmploymentHistory([createEmptyEmployment(formFields)]);
//       setEmploymentUploadedFiles([{}]);
//     }
//   };

//   const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     if (!e.target.files) return;

//     setReplyDocuments(Array.from(e.target.files));
//   };

//   const removeReplyFile = (index: number) => {
//     setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
//   };

//   const handleSubmitReply = async () => {
//     if (!replyRemarks.trim()) {
//       Swal.fire({
//         icon: "warning",
//         title: "Remarks Required",
//         text: "Please enter your remarks before submitting.",
//       });

//       return;
//     }

//     try {
//       const payload = new FormData();

//       payload.append(
//         "assignment_template_id",
//         assignment_template_id ?? assignmentTemplateId ?? "",
//       );

//       payload.append("assignment_id", assignment_id ?? assignmentId ?? "");

//       payload.append("case_id", case_id ?? caseId ?? "");

//       payload.append("candidate_id", candidate_id ?? candidateId ?? "");

//       payload.append("actor_due_on", actor_type ?? "CANDIDATE");

//       payload.append("remarks", replyRemarks);

//       replyDocuments.forEach((file) => {
//         payload.append("documents", file);
//       });

//       const res = await insuffResponse.callApi(payload);

//       if (res?.success) {
//         Swal.fire({
//           icon: "success",
//           title: "Success",
//           text: "Your response has been submitted successfully.",
//         });

//         navigate(all_routes.caseSummary);
//       }
//     } catch (error: any) {
//       Swal.fire({
//         icon: "error",
//         title: "Submission Failed",
//         text: error?.message || "Unable to submit your response.",
//       });
//     }
//   };

//   const sortedNormalFields = formFields
//     .filter((field) => field.type !== "file")
//     .slice()
//     .sort((a, b) => a.display_order - b.display_order);

//   const sortedFileFields = formFields
//     .filter((field) => field.type === "file")
//     .slice()
//     .sort((a, b) => a.display_order - b.display_order);

//   const isValidDate = (value: any): value is string => {
//     return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
//   };

//   return (
//     <>
//       <div className="page-wrapper">
//         <div className="content">
//           <div className="row">
//             <div className="col-md-8">
//               {formLoading ? (
//                 <SnipperLoader />
//               ) : (
//                 <div className="card overflow-hidden">
//                   {/* Header */}
//                   <div
//                     className="border-bottom px-4 py-3"
//                     style={{
//                       backgroundColor: "#FAFAFA",
//                     }}
//                   >
//                     <h5
//                       className="fw-semibold mb-1"
//                       style={{
//                         color: "#101828",
//                         fontSize: "17px",
//                       }}
//                     >
//                       {checkDetails?.check_name}
//                     </h5>

//                     {checkDetails?.check_desc && (
//                       <p
//                         className="mb-0"
//                         style={{
//                           color: "#667085",
//                           fontSize: "13px",
//                           lineHeight: "20px",
//                         }}
//                       >
//                         {checkDetails?.check_desc}
//                       </p>
//                     )}
//                   </div>

//                   <div className="p-4">
//                     {/* Fresher */}
//                     {/* <div
//                       className="border rounded-3 p-3 mb-4"
//                       style={{
//                         background: "#F8F9FA",
//                         borderColor: "#DEE2E6",
//                       }}
//                     >
//                       <div className="form-check d-flex align-items-start m-0">
//                         <input
//                           id="fresher"
//                           className="form-check-input mt-1"
//                           type="checkbox"
//                           checked={isFresher}
//                           onChange={(e) =>
//                             handleFresherChange(e.target.checked)
//                           }
//                           disabled={isFormDisabled}
//                           style={{
//                             width: "20px",
//                             height: "20px",
//                             cursor: "pointer",
//                           }}
//                         />

//                         <label
//                           htmlFor="fresher"
//                           className="form-check-label ms-3 w-100"
//                           style={{
//                             cursor: "pointer",
//                           }}
//                         >
//                           <div className="d-flex align-items-center gap-2 mb-1">
//                             <span className="fw-semibold text-dark">
//                               I am a Fresher
//                             </span>

//                             <i className="ti ti-briefcase-off text-primary fs-3"></i>
//                           </div>

//                           <small className="text-muted">
//                             Select this option if you do not have any prior work
//                             experience.
//                           </small>
//                         </label>
//                       </div>
//                     </div> */}

//                     {/* Fresher Message */}
//                     {isFresher ? (
//                       <div
//                         className="border rounded-3 p-4 text-center"
//                         style={{
//                           background: "#F8FAFC",
//                           borderColor: "#E4E7EC",
//                         }}
//                       >
//                         <i
//                           className="ti ti-user-check"
//                           style={{
//                             fontSize: "36px",
//                             color: "#0F6E56",
//                           }}
//                         />

//                         <h6 className="fw-semibold mt-2 mb-1">
//                           No Employment History
//                         </h6>

//                         <p className="text-muted small mb-0">
//                           Since you are a fresher, no previous employment
//                           details are required.
//                         </p>
//                       </div>
//                     ) : (
//                       <>
//                         {/* Employment Cards */}
//                         {employmentHistory.map(
//                           (employment, employmentIndex) => {
//                             const isCollapsed =
//                               collapsedEmployments[employmentIndex];

//                             const companyField = formFields.find(
//                               (field) => field.field_id === "company_name",
//                             );

//                             const joiningField = employment["date_of_joining"];
//                             const relievingField =
//                               employment["date_of_relieving"];

//                             return (
//                               <div
//                                 key={employmentIndex}
//                                 className="border rounded-3 mb-4 overflow-hidden"
//                                 style={{
//                                   background: "#FFFFFF",
//                                   borderColor: "#D0D5DD",
//                                 }}
//                               >
//                                 {/* Card Header */}
//                                 <div
//                                   className="px-4 py-3 d-flex align-items-center justify-content-between"
//                                   style={{
//                                     background: "#F8FAFC",
//                                     borderBottom: isCollapsed
//                                       ? "none"
//                                       : "1px solid #EAECF0",
//                                     cursor: "pointer",
//                                   }}
//                                   onClick={() =>
//                                     toggleEmployment(employmentIndex)
//                                   }
//                                 >
//                                   <div className="d-flex align-items-center gap-3">
//                                     <div
//                                       className="d-flex align-items-center justify-content-center rounded-circle"
//                                       style={{
//                                         width: "34px",
//                                         height: "34px",
//                                         background: "#E1F5EE",
//                                         color: "#0F6E56",
//                                         fontWeight: 600,
//                                       }}
//                                     >
//                                       {employmentIndex + 1}
//                                     </div>

//                                     <div>
//                                       <h6 className="fw-semibold mb-1">
//                                         Employment {employmentIndex + 1}
//                                       </h6>

//                                       <div
//                                         className="text-muted small"
//                                         style={{
//                                           maxWidth: "450px",
//                                         }}
//                                       >
//                                         {companyField
//                                           ? employment[companyField.field_id] ||
//                                             "Add employment details"
//                                           : "Add employment details"}

//                                         {joiningField && " - " + joiningField}

//                                         {relievingField &&
//                                           " to " + relievingField}
//                                       </div>
//                                     </div>
//                                   </div>

//                                   <div className="d-flex align-items-center gap-2">
//                                     {employmentHistory.length > 1 && (
//                                       <button
//                                         type="button"
//                                         className="btn btn-sm btn-light text-danger"
//                                         onClick={(e) => {
//                                           e.stopPropagation();

//                                           removeEmployment(employmentIndex);
//                                         }}
//                                         disabled={isFormDisabled}
//                                       >
//                                         <i className="ti ti-trash me-1"></i>
//                                         Remove
//                                       </button>
//                                     )}

//                                     <button
//                                       type="button"
//                                       className="btn btn-sm btn-primary"
//                                       onClick={(e) => {
//                                         e.stopPropagation();

//                                         toggleEmployment(employmentIndex);
//                                       }}
//                                     >
//                                       <i
//                                         className={`ti ${
//                                           isCollapsed
//                                             ? "ti-chevron-down"
//                                             : "ti-chevron-up"
//                                         }`}
//                                       ></i>
//                                     </button>
//                                   </div>
//                                 </div>

//                                 {!isCollapsed && (
//                                   <div className="p-4">
//                                     {/* Dynamic Fields */}
//                                     <div className="row mb-3 ">
//                                       {sortedNormalFields.map((field) => {
//                                         const errorKey = getRecordKey(
//                                           employmentIndex,
//                                           field.field_id,
//                                         );

//                                         const fieldValue =
//                                           employment[field.field_id] ?? "";

//                                         return (
//                                           <div
//                                             key={`${employmentIndex}_${field.field_uuid}`}
//                                             className="col-md-6 d-flex flex-column"
//                                           >
//                                             <label className="form-label fw-medium">
//                                               {field.label}

//                                               {field.required && (
//                                                 <span className="text-danger ms-1">
//                                                   *
//                                                 </span>
//                                               )}
//                                             </label>

//                                             {field.type === "dropdown" ? (
//                                               <select
//                                                 className={`form-select ${
//                                                   errors[errorKey]
//                                                     ? "is-invalid"
//                                                     : ""
//                                                 }`}
//                                                 value={
//                                                   isFresher ? "N/A" : fieldValue
//                                                 }
//                                                 required={field.required}
//                                                 disabled={isFormDisabled}
//                                                 onChange={(e) =>
//                                                   handleFieldChange(
//                                                     employmentIndex,
//                                                     field.field_id,
//                                                     e.target.value,
//                                                   )
//                                                 }
//                                               >
//                                                 <option value="">
//                                                   {field.placeholder ||
//                                                     "Select"}
//                                                 </option>

//                                                 {field.options?.map(
//                                                   (option: any) => (
//                                                     <option
//                                                       key={option.value}
//                                                       value={option.value}
//                                                     >
//                                                       {option.label}
//                                                     </option>
//                                                   ),
//                                                 )}
//                                               </select>
//                                             ) : (
//                                               <input
//                                                 type={field.type}
//                                                 className={`form-control ${
//                                                   errors[errorKey]
//                                                     ? "is-invalid"
//                                                     : ""
//                                                 }`}
//                                                 placeholder={field.placeholder}
//                                                 value={
//                                                   isFresher ? "N/A" : fieldValue
//                                                 }
//                                                 required={field.required}
//                                                 disabled={isFormDisabled}
//                                                 onChange={(e) =>
//                                                   handleFieldChange(
//                                                     employmentIndex,
//                                                     field.field_id,
//                                                     e.target.value,
//                                                   )
//                                                 }
//                                                 min={
//                                                   field.field_id ===
//                                                     "date_of_relieving" &&
//                                                   isValidDate(joiningField)
//                                                     ? joiningField
//                                                     : undefined
//                                                 }
//                                                 max={
//                                                   field.field_id ===
//                                                   "date_of_joining"
//                                                     ? isValidDate(
//                                                         relievingField,
//                                                       )
//                                                       ? relievingField <
//                                                         getTodayDate()
//                                                         ? relievingField
//                                                         : getTodayDate()
//                                                       : getTodayDate()
//                                                     : field.field_id ===
//                                                         "date_of_relieving"
//                                                       ? getYesterdayDate()
//                                                       : undefined
//                                                 }
//                                                 pattern={
//                                                   field?.validation_rules
//                                                     ?.pattern
//                                                 }
//                                                 maxLength={
//                                                   field?.validation_rules
//                                                     ?.maxLength
//                                                 }
//                                                 minLength={
//                                                   field?.validation_rules
//                                                     ?.minLength
//                                                 }
//                                               />
//                                             )}

//                                             {errors[errorKey] && (
//                                               <div className="invalid-feedback d-block">
//                                                 {errors[errorKey]}
//                                               </div>
//                                             )}
//                                           </div>
//                                         );
//                                       })}
//                                     </div>

//                                     {/* Documents */}
//                                     {sortedFileFields.length > 0 && (
//                                       <div className="border-top pt-4 mt-2">
//                                         <div className="d-flex align-items-center justify-content-between mb-3">
//                                           <div>
//                                             <h6 className="fw-bold mb-1">
//                                               Employment Documents
//                                             </h6>

//                                             <small className="text-muted">
//                                               Upload documents for Employment{" "}
//                                               {employmentIndex + 1}
//                                             </small>
//                                           </div>
//                                         </div>

//                                         <div
//                                           className="rounded-3 p-3 mb-4"
//                                           style={{
//                                             background: "#F8FAFC",
//                                             border: "1px solid #EAECF0",
//                                           }}
//                                         >
//                                           <small className="text-muted">
//                                             <strong>Instructions:</strong>
//                                           </small>

//                                           <ul className="small text-muted mb-0 mt-2 ps-3">
//                                             <li>
//                                               Kindly ensure the document is
//                                               readable and not pixelated or
//                                               blurred.
//                                             </li>
//                                             <li>
//                                               Accepted formats: PDF, JPEG, JPG,
//                                               PNG, DOC, DOCX.
//                                             </li>
//                                             <li>
//                                               Document size should not exceed
//                                               5MB.
//                                             </li>
//                                           </ul>
//                                         </div>

//                                         {sortedFileFields.map((field) => {
//                                           const key = getRecordKey(
//                                             employmentIndex,
//                                             field.field_id,
//                                           );

//                                           // const selectedFile =
//                                           //   employment[field.field_id];

//                                           // const isNoDocument = noDocument[key];

//                                           // const uploadedFile =
//                                           //   field.uploaded_files?.[0];
//                                           const selectedFile =
//                                             employment[field.field_id];

//                                           const isNoDocument = noDocument[key];

//                                           const uploadedFiles =
//                                             employmentUploadedFiles[
//                                               employmentIndex
//                                             ]?.[field.field_id] || [];

//                                           return (
//                                             <div
//                                               key={`${employmentIndex}_${field.field_uuid}`}
//                                               className="row align-items-center mb-4 pb-4 border-bottom"
//                                             >
//                                               <div className="col-lg-7">
//                                                 <label className="form-label fw-semibold">
//                                                   {field.label}

//                                                   {field.required && (
//                                                     <span className="text-danger ms-1">
//                                                       *
//                                                     </span>
//                                                   )}
//                                                 </label>

//                                                 <input
//                                                   ref={(el) => {
//                                                     fileInputRefs.current[key] =
//                                                       el;
//                                                   }}
//                                                   type="file"
//                                                   className={`form-control ${
//                                                     errors[key]
//                                                       ? "is-invalid"
//                                                       : ""
//                                                   }`}
//                                                   multiple={
//                                                     field.file_config
//                                                       ?.multiple === true
//                                                   }
//                                                   accept={field.file_config?.allowedTypes
//                                                     ?.map(
//                                                       (type: string) =>
//                                                         `.${type}`,
//                                                     )
//                                                     .join(",")}
//                                                   onChange={(e) => {
//                                                     if (
//                                                       field.file_config
//                                                         ?.multiple
//                                                     ) {
//                                                       handleFieldChange(
//                                                         employmentIndex,
//                                                         field.field_id,
//                                                         Array.from(
//                                                           e.target.files || [],
//                                                         ),
//                                                       );
//                                                     } else {
//                                                       handleFieldChange(
//                                                         employmentIndex,
//                                                         field.field_id,
//                                                         e.target.files?.[0] ||
//                                                           null,
//                                                       );
//                                                     }
//                                                   }}
//                                                   disabled={
//                                                     isNoDocument ||
//                                                     isFormDisabled
//                                                   }
//                                                 />

//                                                 {errors[key] && (
//                                                   <div className="invalid-feedback d-block">
//                                                     {errors[key]}
//                                                   </div>
//                                                 )}

//                                                 <div className="form-check mt-2">
//                                                   <input
//                                                     className="form-check-input"
//                                                     type="checkbox"
//                                                     checked={
//                                                       isNoDocument || false
//                                                     }
//                                                     onChange={(e) =>
//                                                       handleNoDocumentChange(
//                                                         employmentIndex,
//                                                         field.field_id,
//                                                         e.target.checked,
//                                                       )
//                                                     }
//                                                     disabled={isFormDisabled}
//                                                   />

//                                                   <label className="form-check-label text-muted small">
//                                                     I do not have this document
//                                                   </label>
//                                                 </div>
//                                               </div>

//                                               <div className="col-lg-5 text-center mt-3 mt-lg-0">
//                                                 {/* ========================================== */}
//                                                 {/* NEWLY SELECTED MULTIPLE FILES */}
//                                                 {/* ========================================== */}

//                                                 {Array.isArray(selectedFile) &&
//                                                 selectedFile.length > 0 ? (
//                                                   <>
//                                                     <i
//                                                       className="bi bi-file-earmark-pdf-fill text-danger"
//                                                       style={{
//                                                         fontSize: "50px",
//                                                         cursor: "pointer",
//                                                       }}
//                                                     />

//                                                     {selectedFile.map(
//                                                       (
//                                                         file: File,
//                                                         index: number,
//                                                       ) => (
//                                                         <p
//                                                           key={`${file.name}-${index}`}
//                                                           className="text-danger small mb-0 text-truncate"
//                                                           title={file.name}
//                                                         >
//                                                           {file.name}
//                                                         </p>
//                                                       ),
//                                                     )}
//                                                   </>
//                                                 ) : selectedFile instanceof
//                                                   File ? (
//                                                   /* ========================================== */
//                                                   /* NEWLY SELECTED SINGLE FILE */
//                                                   /* ========================================== */

//                                                   <>
//                                                     <i
//                                                       className="bi bi-file-earmark-pdf-fill text-danger"
//                                                       style={{
//                                                         fontSize: "50px",
//                                                         cursor: "pointer",
//                                                       }}
//                                                     />

//                                                     <p
//                                                       className="text-danger small mb-0 text-truncate"
//                                                       title={selectedFile.name}
//                                                     >
//                                                       {selectedFile.name}
//                                                     </p>
//                                                   </>
//                                                 ) : uploadedFiles.length > 0 ? (
//                                                   /* ========================================== */
//                                                   /* EXISTING UPLOADED FILES */
//                                                   /* ========================================== */

//                                                   <>
//                                                     <i
//                                                       className="bi bi-file-earmark-pdf-fill text-danger"
//                                                       style={{
//                                                         fontSize: "48px",
//                                                       }}
//                                                     />

//                                                     {uploadedFiles.map(
//                                                       (
//                                                         file: any,
//                                                         index: number,
//                                                       ) => (
//                                                         <a
//                                                           key={
//                                                             file.id ??
//                                                             `${file.original_name}-${index}`
//                                                           }
//                                                           href={`${apibaseurl}${file.url}`}
//                                                           target="_blank"
//                                                           rel="noopener noreferrer"
//                                                           className="text-decoration-none d-block"
//                                                         >
//                                                           <p
//                                                             className="text-danger small mb-0 text-truncate"
//                                                             title={
//                                                               file.original_name ||
//                                                               file.name
//                                                             }
//                                                           >
//                                                             {file.original_name ||
//                                                               file.name ||
//                                                               `Document ${index + 1}`}
//                                                           </p>
//                                                         </a>
//                                                       ),
//                                                     )}
//                                                   </>
//                                                 ) : (
//                                                   /* ========================================== */
//                                                   /* NO FILE */
//                                                   /* ========================================== */

//                                                   <>
//                                                     <i
//                                                       className="bi bi-file-earmark-pdf-fill text-danger"
//                                                       style={{
//                                                         fontSize: "48px",
//                                                       }}
//                                                     />

//                                                     <p className="text-muted small mb-0">
//                                                       No File Selected
//                                                     </p>
//                                                   </>
//                                                 )}
//                                               </div>
//                                             </div>
//                                           );
//                                         })}
//                                       </div>
//                                     )}
//                                   </div>
//                                 )}
//                               </div>
//                             );
//                           },
//                         )}

//                         {/* Add Employment */}
//                         <button
//                           type="button"
//                           className="w-100 mb-4"
//                           onClick={addEmployment}
//                           disabled={isFormDisabled}
//                           style={{
//                             border: "1px dashed #98A2B3",
//                             background: "#F8FAFC",
//                             borderRadius: "10px",
//                             padding: "14px 20px",
//                             cursor: isFormDisabled ? "not-allowed" : "pointer",
//                             transition: "all 0.2s ease",
//                           }}
//                           onMouseEnter={(e) => {
//                             if (!isFormDisabled) {
//                               e.currentTarget.style.background = "#F0FDF9";
//                               e.currentTarget.style.borderColor = "#0F6E56";
//                             }
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.background = "#F8FAFC";
//                             e.currentTarget.style.borderColor = "#98A2B3";
//                           }}
//                         >
//                           <div className="d-flex align-items-center justify-content-center gap-3">
//                             <div
//                               className="d-flex align-items-center justify-content-center rounded-circle"
//                               style={{
//                                 width: "34px",
//                                 height: "34px",
//                                 background: "#E1F5EE",
//                                 color: "#0F6E56",
//                                 flexShrink: 0,
//                               }}
//                             >
//                               <i
//                                 className="ti ti-plus"
//                                 style={{ fontSize: "18px" }}
//                               ></i>
//                             </div>

//                             <div className="text-start">
//                               <div
//                                 className="fw-semibold"
//                                 style={{
//                                   color: "#344054",
//                                   fontSize: "14px",
//                                 }}
//                               >
//                                 Add Previous Employment
//                               </div>

//                               <div
//                                 style={{
//                                   color: "#667085",
//                                   fontSize: "12px",
//                                   marginTop: "2px",
//                                 }}
//                               >
//                                 Add details of another company
//                               </div>
//                             </div>
//                           </div>
//                         </button>
//                       </>
//                     )}

//                     {/* Save */}
//                     {!isFormDisabled &&
//                       templateStatus !== "CANDIDATE_INSUFF" && (
//                         <div className="d-flex gap-2 align-items-center mt-4">
//                           <button
//                             type="button"
//                             className="btn btn-primary"
//                             onClick={handleSaveDocument}
//                             disabled={templateSaveAPI.loading}
//                           >
//                             {templateSaveAPI.loading ? (
//                               <>
//                                 <span
//                                   className="spinner-border spinner-border-sm me-2"
//                                   role="status"
//                                   aria-hidden="true"
//                                 ></span>
//                                 Saving...
//                               </>
//                             ) : (
//                               <>
//                                 <i className="ti ti-device-floppy me-2"></i>
//                                 Save
//                               </>
//                             )}
//                           </button>
//                         </div>
//                       )}

//                     {/* Insufficiency Update */}
//                     {templateStatus === "CANDIDATE_INSUFF" && (
//                       <div className="d-flex gap-2 align-items-center mt-4">
//                         <button
//                           type="button"
//                           className="btn btn-primary"
//                           onClick={handleUpdateDocument}
//                           disabled={templateUpdateAPI.loading}
//                         >
//                           {templateUpdateAPI.loading ? (
//                             <>
//                               <span
//                                 className="spinner-border spinner-border-sm me-2"
//                                 role="status"
//                                 aria-hidden="true"
//                               ></span>
//                               Updating...
//                             </>
//                           ) : (
//                             <>
//                               <i className="ti ti-upload me-2"></i>
//                               Save Form & Documents
//                             </>
//                           )}
//                         </button>

//                         <button
//                           type="button"
//                           className="btn btn-success"
//                           onClick={() => setShowReplyModal(true)}
//                         >
//                           <i className="ti ti-message-circle me-2"></i>
//                           Reply to Insufficiency
//                         </button>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               )}
//             </div>
//             <div className="col-md-4">
//               <div
//                 style={{
//                   position: "sticky",
//                   top: "60px",
//                   alignSelf: "flex-start",
//                 }}
//               >
//                 <div className="card border-0 shadow-sm">
//                   {/* Header */}
//                   <div
//                     className="text-center py-4"
//                     style={{ backgroundColor: "#f7f1e3" }}
//                   >
//                     <div
//                       className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
//                       style={{ width: "70px", height: "70px" }}
//                     >
//                       {/* <i
//                                   className="bi bi-lightbulb-fill text-warning"
//                                   style={{ fontSize: "35px" }}
//                                 ></i> */}
//                       <img src={bulbImage} alt="buld" />
//                     </div>

//                     <h6 className="fw-bold mb-0">Instructions</h6>
//                   </div>

//                   {/* Body */}
//                   <div className="card-body">
//                     {/* Instruction 1 */}
//                     <div className="d-flex mb-4">
//                       <div
//                         className="rounded me-3 mt-1 flex-shrink-0"
//                         style={{
//                           width: "16px",
//                           height: "16px",
//                           backgroundColor: "#f4c542",
//                         }}
//                       ></div>

//                       <div>
//                         <h6 className="mb-1 fw-semibold">Instruction 1</h6>

//                         <p className="text-muted mb-0">
//                           Please provide your relevant employment details as
//                           mentioned on your official document (Experience
//                           Letter, Relieving Letter, Offer Letter) in the form.
//                           Please start with your previous employer prior to
//                           joining (Teleperformance Global Services Private
//                           Limited).{" "}
//                         </p>
//                       </div>
//                     </div>

//                     {/* Instruction 2 */}
//                     <div className="d-flex mb-4">
//                       <div
//                         className="rounded me-3 mt-1 flex-shrink-0"
//                         style={{
//                           width: "16px",
//                           height: "16px",
//                           backgroundColor: "#f4c542",
//                         }}
//                       ></div>
//                       <div>
//                         <h6 className="mb-1 fw-semibold">Instruction 2</h6>

//                         <p className="text-muted mb-0">
//                           You may be required to attach a copy of the Employment
//                           Documents (Appointment Letter, Relieving Letter,
//                           Experience Letter). Kindly ensure the
//                           photocopy/picture is readable, and is not
//                           cut/pixelated/blurred. The required document set will
//                           be displayed in the upload documents sections.
//                         </p>
//                       </div>
//                     </div>
//                     {/* Instruction 2 */}
//                     <div className="d-flex mb-4">
//                       <div
//                         className="rounded me-3 mt-1 flex-shrink-0"
//                         style={{
//                           width: "16px",
//                           height: "16px",
//                           backgroundColor: "#f4c542",
//                         }}
//                       ></div>
//                       <div>
//                         <h6 className="mb-1 fw-semibold">Instruction 3</h6>

//                         <p className="text-muted mb-0">
//                           Note: We will reach out to you immediate/previous
//                           employer, once you leave the company and provide us
//                           the relevant documentation to proceed ahead with the
//                           verification.
//                         </p>
//                       </div>
//                     </div>
//                     {/* Instruction 2 */}
//                     <div className="d-flex mb-4">
//                       <div
//                         className="rounded me-3 mt-1 flex-shrink-0"
//                         style={{
//                           width: "16px",
//                           height: "16px",
//                           backgroundColor: "#f4c542",
//                         }}
//                       ></div>
//                       <div>
//                         <h6 className="mb-1 fw-semibold">Instruction 4</h6>

//                         <p className="text-muted mb-0">
//                           In case we are unable to obtain verification from your
//                           HR/Supervisor due to non-responsiveness, we can
//                           utilise the data from your Form 16 to validate your
//                           employment .{" "}
//                         </p>
//                       </div>
//                     </div>
//                     <div className="d-flex mb-4">
//                       <div
//                         className="rounded me-3 mt-1 flex-shrink-0"
//                         style={{
//                           width: "16px",
//                           height: "16px",
//                           backgroundColor: "#f4c542",
//                         }}
//                       ></div>
//                       <div>
//                         <h6 className="mb-1 fw-semibold">Instruction 5</h6>

//                         <p className="text-muted mb-0">
//                           It is recommended to provide form 16 for any
//                           assessment year pertaining to the employer’s
//                           details.{" "}
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Reply to Insufficiency Modal */}
//           {showReplyModal && (
//             <div
//               className="modal fade show d-block"
//               style={{
//                 background: "rgba(0,0,0,.5)",
//               }}
//             >
//               <div className="modal-dialog modal-lg modal-dialog-centered">
//                 <div className="modal-content border-0 shadow">
//                   <div className="modal-header">
//                     <div>
//                       <h5 className="modal-title fw-bold mb-1">
//                         Respond to Insufficiency
//                       </h5>

//                       <small className="text-muted">
//                         Please provide your explanation and upload supporting
//                         documents if applicable.
//                       </small>
//                     </div>

//                     <button
//                       className="btn-close"
//                       onClick={() => setShowReplyModal(false)}
//                     />
//                   </div>

//                   <div className="modal-body">
//                     <div className="mb-4">
//                       <label className="form-label fw-semibold">
//                         Remarks
//                         <span className="text-danger ms-1">*</span>
//                       </label>

//                       <textarea
//                         rows={5}
//                         className="form-control"
//                         placeholder="Enter your response to the verifier..."
//                         value={replyRemarks}
//                         onChange={(e) => setReplyRemarks(e.target.value)}
//                         style={{
//                           resize: "none",
//                         }}
//                       />

//                       <small className="text-muted">
//                         Clearly explain the requested information or
//                         clarification.
//                       </small>
//                     </div>

//                     <div className="mb-3">
//                       <label className="form-label fw-semibold">
//                         Supporting Documents
//                       </label>

//                       <input
//                         type="file"
//                         className="form-control"
//                         multiple
//                         accept=".jpg,.jpeg,.png,.pdf"
//                         onChange={handleReplyFileChange}
//                       />

//                       <small className="text-muted">
//                         Accepted formats: JPG, JPEG, PNG, PDF. Maximum 10 MB per
//                         file.
//                       </small>
//                     </div>

//                     {replyDocuments.length > 0 && (
//                       <div className="mt-3">
//                         <label className="fw-semibold mb-2">
//                           Selected Files
//                         </label>

//                         <div className="d-flex flex-wrap gap-2">
//                           {replyDocuments.map((file, index) => (
//                             <span
//                               key={index}
//                               className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
//                             >
//                               <i className="ti ti-paperclip me-2"></i>

//                               <span
//                                 className="text-truncate"
//                                 style={{
//                                   maxWidth: 180,
//                                 }}
//                               >
//                                 {file.name}
//                               </span>

//                               <button
//                                 type="button"
//                                 className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
//                                 onClick={() => removeReplyFile(index)}
//                               >
//                                 <i className="ti ti-x"></i>
//                               </button>
//                             </span>
//                           ))}
//                         </div>
//                       </div>
//                     )}
//                   </div>

//                   <div className="modal-footer gap-3">
//                     <button
//                       className="btn btn-light border"
//                       onClick={() => setShowReplyModal(false)}
//                     >
//                       Cancel
//                     </button>

//                     <button
//                       className="btn btn-primary"
//                       onClick={handleSubmitReply}
//                       disabled={insuffResponse.loading}
//                     >
//                       {insuffResponse.loading ? (
//                         <>
//                           <span
//                             className="spinner-border spinner-border-sm me-2"
//                             role="status"
//                             aria-hidden="true"
//                           ></span>
//                           Submitting...
//                         </>
//                       ) : (
//                         <>
//                           <i className="ti ti-send me-2"></i>
//                           Submit Response
//                         </>
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>

//         <Footer />
//       </div>
//     </>
//   );
// };

// export default EmployementHistory;
